import { useCallback, useMemo, useState } from 'react';
import type { Patient } from '@/stores/store.types';

const getNextTagFilter = (
  tagFilter: string[],
  tagId: string,
  isSelected: boolean,
) =>
  isSelected
    ? tagFilter.includes(tagId)
      ? tagFilter
      : [...tagFilter, tagId]
    : tagFilter.filter((id) => id !== tagId);

export const usePatientFilter = <T extends Patient>(
  patients: T[],
  onFilterCommit?: () => void,
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilterState] = useState<string[]>([]);

  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedTagFilter, setAppliedTagFilter] = useState<string[]>([]);

  const countFilterApplied = useMemo(() => {
    let count = 0;

    if (appliedTagFilter.length > 0) {
      count += appliedTagFilter.length;
    }

    if (appliedSearchTerm.trim() !== '') {
      count += 1;
    }

    return count;
  }, [appliedTagFilter, appliedSearchTerm]);

  const setTagFilter = useCallback((tagId: string, isSelected: boolean) => {
    setTagFilterState((currentTagFilter) =>
      getNextTagFilter(currentTagFilter, tagId, isSelected),
    );
  }, []);

  const applyFilter = useCallback(() => {
    setAppliedSearchTerm(searchTerm);
    setAppliedTagFilter(tagFilter);
    onFilterCommit?.();
  }, [searchTerm, tagFilter, onFilterCommit]);

  const resetFilter = useCallback(() => {
    setSearchTerm('');
    setTagFilterState([]);
    setAppliedSearchTerm('');
    setAppliedTagFilter([]);
    onFilterCommit?.();
  }, [onFilterCommit]);

  const filteredPatients = useMemo(() => {
    const normalizedSearchTerm = appliedSearchTerm.trim().toLowerCase();

    return patients.filter((patient) => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();

      const nameMatches = fullName.includes(normalizedSearchTerm);

      const tagMatches =
        appliedTagFilter.length === 0 ||
        (patient.therapistTags ?? []).some((tag) =>
          appliedTagFilter.includes(tag._id),
        );

      return nameMatches && tagMatches;
    });
  }, [patients, appliedSearchTerm, appliedTagFilter]);

  const cancelFilter = useCallback(() => {
    setSearchTerm(appliedSearchTerm);
    setTagFilterState(appliedTagFilter);
  }, [appliedSearchTerm, appliedTagFilter]);

  return {
    filteredPatients,
    countFilterApplied,
    cancelFilter,
    filterProps: {
      searchTerm,
      setSearchTerm,
      tagFilter,
      setTagFilter,
      applyFilter,
      resetFilter,
    },
  };
};
